import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ToolTip from "../../../../parts/tooltip";
import SvgIcon from "../../../../parts/SvgIcon";
import { preserveScrollBeforeNavigation } from "../../../../utils/preserve-scroll-on-navigation";
import { CALORIES_PER_GRAM, formatNutritionValue } from "../../../../utils/nutrition-util";

export const MealPlanCardSimplified = ({
  template,
  heightClass,
  dropdownRef,
  openOptions,
  setOpenOptions,
  handleOptions,
  handleDeleteWeek,
  handleDuplicateWeek,
  handleTemplateAction,
  cardListIcon,
  footerListIcon,
  IDsThatPreserveScroll,
}) => {
  const { t } = useTranslation("coaching-portal");
  const {
    title = "",
    description = "",
    _id: templateId,
    macrosTotal = {},
  } = template;

  // Get actual values from macrosTotal (accumulated from all options)
  const getActualValue = (key) => {
    return macrosTotal[key] || 0;
  };

  // Calculate percentage for macros (not for calories)
  const calculatePercentage = (key, actualValue, totalCalories) => {
    if (key === "calories" || totalCalories === 0) return "0";
    const caloriesPerGram = CALORIES_PER_GRAM[key] || 0;
    if (caloriesPerGram === 0) return "0";
    const macroCalories = actualValue * caloriesPerGram;
    return ((macroCalories / totalCalories) * 100).toFixed(0);
  };

  const totalCalories = getActualValue("calories");

  return (
    <article className="card meal-plan-card--simplified">
      <div>
        <div className="details">
          <header className={`text ${heightClass}`}>
            <span className="title">{title}</span>
            <span className="subtitle">{description}</span>
          </header>
          <div className="content-container">
            {[
              { label: t("allTemplates.mealPlanCardSimplified.proteins"), key: "proteins", className: "protein" },
              { label: t("allTemplates.mealPlanCardSimplified.carbs"), key: "carbs", className: "carbs" },
              { label: t("allTemplates.mealPlanCardSimplified.fats"), key: "fats", className: "fat" },
              { label: t("allTemplates.mealPlanCardSimplified.fibers"), key: "fibers", className: "calories-text" },
              { label: t("allTemplates.mealPlanCardSimplified.calories"), key: "calories", className: "calories-text" },
            ].map(({ label, key, className }) => {
              const actualValue = getActualValue(key);
              const actual = formatNutritionValue(actualValue);
              const percentage = calculatePercentage(key, actualValue, totalCalories);

              return (
                <div className="info" key={label}>
                  <div className="flex flex-col gap-0.5">
                    <span className={className}>{label}</span>
                  </div>
                  <div className="goals">
                    <span>
                      {actual}
                      {key === "calories" ? " kcal" : "g"}
                      {percentage !== "0" && (
                        <span className="text-[0.75rem] opacity-70">
                          {" "}
                          ({percentage}%)
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <footer className="footer">
          <a
            className="edit-card"
            data-button-type="text"
            data-icon
            href={`/coaching-portal/templates/edit-meal-plan?mealPlan=${templateId}`}
          >
            <img alt={t("allTemplates.mealPlanCardSimplified.addAlt")} src="/img/new-icons/edit-gradient-icon.svg" />
            <span className="clipped font-semibold">{t("allTemplates.mealPlanTemplates.editMeal")}</span>
          </a>
          <div className="icons">
            {footerListIcon.map(({ src, alt, id, url, message }) => {
              const finalUrl =
                id === "copy-meal-plan-from" ||
                id === "copy-meal-plan-to" ||
                id === "meal-plan-settings"
                  ? `${url}&templateId=${templateId}`
                  : id === "preview-template"
                  ? `${url}?mealPlan=${templateId}&viewMode=true`
                  : url;

              return (
                <ToolTip
                  id={`${templateId}-${id}`}
                  body={message}
                  key={`${templateId}-${id}`}
                >
                  <a
                    href={
                      [
                        "delete-template",
                        "duplicate-template",
                      ].includes(id)
                        ? ""
                        : finalUrl
                    }
                    onClick={(e) => {
                      if (
                        [
                          "delete-template",
                          "duplicate-template",
                        ].includes(id)
                      ) {
                        e.preventDefault();
                        handleTemplateAction(templateId, id);
                      }
                      if (IDsThatPreserveScroll.includes(id)) {
                        preserveScrollBeforeNavigation();
                      }
                    }}
                  >
                    <div
                      className="bg-(--white-05) p-1 rounded-(--border-radius-md) hover:bg-(--white-20) cursor-pointer"
                      id="img-footer-cycle-card-container"
                    >
                      <SvgIcon
                        src={src}
                        alt={alt}
                        noFilterOnLight={id === "delete-template"}
                      />
                    </div>
                  </a>
                </ToolTip>
              );
            })}
          </div>
        </footer>
      </div>
    </article>
  );
};

MealPlanCardSimplified.propTypes = {
  template: PropTypes.object.isRequired,
  heightClass: PropTypes.string.isRequired,
  dropdownRef: PropTypes.object.isRequired,
  openOptions: PropTypes.object,
  setOpenOptions: PropTypes.func.isRequired,
  handleOptions: PropTypes.func.isRequired,
  handleDeleteWeek: PropTypes.func.isRequired,
  handleDuplicateWeek: PropTypes.func.isRequired,
  handleTemplateAction: PropTypes.func.isRequired,
  cardListIcon: PropTypes.array.isRequired,
  footerListIcon: PropTypes.array.isRequired,
  IDsThatPreserveScroll: PropTypes.array.isRequired,
};
